import { isNullishOrBlank, isRecord, parseJsonStrict } from './configurationNormalizationPrimitives';

/** Builder field types that hold JSON: `json` takes an object or an array, `json_query` takes an object. */
export type JsonFieldType = 'json' | 'json_query';

export type JsonQuery = Record<string, unknown>;

type JsonFieldValue = JsonQuery | unknown[];

type JsonFieldText = { kind: 'blank' } | { kind: 'valid'; value: JsonFieldValue } | { kind: 'invalid' };

const EXPECTED_SHAPE: Record<JsonFieldType, string> = {
  json: 'a JSON object or array',
  json_query: 'a JSON object',
};

export const isJsonFieldType = (type: unknown): type is JsonFieldType => type === 'json' || type === 'json_query';

const accepts = (type: JsonFieldType, value: unknown): value is JsonFieldValue => isRecord(value) || (type === 'json' && Array.isArray(value));

/** An accepted value, or a JSON string that parses to one. Anything else, blank included, is null. */
export const toJsonFieldValue = (type: JsonFieldType, value: unknown): JsonFieldValue | null => {
  if (accepts(type, value)) return value;
  if (typeof value !== 'string' || isNullishOrBlank(value)) return null;
  const parsed = parseJsonStrict(value, 'JSON');
  return parsed.ok && accepts(type, parsed.value) ? parsed.value : null;
};

/** Blank text (whitespace included) is no value, accepted JSON is valid, and anything else is invalid. */
export const parseJsonFieldText = (type: JsonFieldType, text: string): JsonFieldText => {
  if (isNullishOrBlank(text)) return { kind: 'blank' };
  const value = toJsonFieldValue(type, text);
  return value ? { kind: 'valid', value } : { kind: 'invalid' };
};

/** What the field stores for its text: '' when blank, the parsed value when valid, and the raw text otherwise so saving can reject it. */
export const jsonFieldWriteValue = (type: JsonFieldType, text: string): JsonFieldValue | string => {
  const parsed = parseJsonFieldText(type, text);
  if (parsed.kind === 'blank') return '';
  return parsed.kind === 'valid' ? parsed.value : text;
};

/**
 * Editor text for a stored value. Blank for nullish or whitespace, 2-space JSON for accepted JSON, other text as-is, and
 * any other value as JSON so an invalid stored value stays visible instead of reading as no value.
 */
export const formatJsonFieldText = (type: JsonFieldType, value: unknown): string => {
  if (isNullishOrBlank(value)) return '';
  const accepted = toJsonFieldValue(type, value);
  if (accepted) return JSON.stringify(accepted, null, 2);
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2) ?? String(value);
};

/** Identity of a stored value or typed text that is the same for a JSON string and its parsed value. */
export const jsonFieldValueKey = (type: JsonFieldType, value: unknown): string => {
  if (isNullishOrBlank(value)) return '';
  const accepted = toJsonFieldValue(type, value);
  if (accepted) return `valid:${JSON.stringify(accepted)}`;
  if (typeof value === 'string') return `text:${value}`;
  return `value:${JSON.stringify(value) ?? String(value)}`;
};

/** The save error for a stored value, or null when it is blank or holds accepted JSON (which keeps its stored form). */
export const jsonFieldValueError = (type: JsonFieldType, value: unknown, path: string): string | null => {
  if (isNullishOrBlank(value) || toJsonFieldValue(type, value)) return null;
  return `${path} must be ${EXPECTED_SHAPE[type]}.`;
};

type SchemaNode = { type?: unknown; properties?: unknown; items?: unknown };

/**
 * Save errors for every json or json_query value inside `value`, following `schema` the way the json-builder renders
 * it: declared `properties`, else `items` for every key of a dictionary object, and `items` for array entries. JSON
 * values are checked whole and never descended into, a container stored as a JSON string is parsed only to walk it,
 * and nothing is changed.
 */
export const collectJsonFieldErrors = (schema: unknown, value: unknown, path: string): string[] => {
  if (!isRecord(schema)) return [];
  const node = schema as SchemaNode;
  if (isJsonFieldType(node.type)) {
    const error = jsonFieldValueError(node.type, value, path);
    return error ? [error] : [];
  }

  const parsed = typeof value === 'string' ? parseJsonStrict(value, 'JSON') : null;
  const container = parsed?.ok ? parsed.value : value;
  if (Array.isArray(container)) {
    return container.flatMap((item, index) => collectJsonFieldErrors(node.items, item, `${path}[${index}]`));
  }
  if (isRecord(container)) {
    const properties = isRecord(node.properties) ? node.properties : undefined;
    return Object.entries(container).flatMap(([key, item]) =>
      collectJsonFieldErrors(properties ? properties[key] : node.items, item, `${path}.${key}`)
    );
  }
  return [];
};
